import useOperationQueries from 'api/operation/common/useOperationQueries';
import useGetMovementWorkspace from 'api/operation/movement/useGetMovementWorkspace';
import { useMovementWorkspaceMutations } from 'api/operation/movement/useMovementWorkspaceMutations';
import { useCallback, useEffect, useMemo, useState } from 'react';

const useMovementWorkspace = guid => {
  const { getMovementWorkspace, loading, setWorkspace, workspace } =
    useGetMovementWorkspace();
  const mutations = useMovementWorkspaceMutations();
  const { getAvailableAgents } = useOperationQueries();
  const [actionLoading, setActionLoading] = useState(false);

  const canRefetch = useMemo(
    () => guid && location.pathname.startsWith('/operation/movement'),
    [guid],
  );

  useEffect(() => {
    if (!canRefetch) return;
    getMovementWorkspace(guid);
  }, [guid, getMovementWorkspace, canRefetch]);

  const refreshWorkspace = useCallback(() => {
    if (!guid) return Promise.resolve(null);
    return getMovementWorkspace(guid);
  }, [getMovementWorkspace, guid]);

  const withAction = useCallback(
    async action => {
      setActionLoading(true);
      try {
        const result = await action();
        if (result) await refreshWorkspace();
        return result;
      } finally {
        setActionLoading(false);
      }
    },
    [refreshWorkspace],
  );

  const {
    title = '',
    agent = null,
    product = null,
    origin = null,
    destination = null,
    taskGuid = null,
    taskState,
    state: rawState,
  } = workspace ?? {};
  const state = taskState ?? rawState;

  const isPreview = state === 4;
  const canMutate = isPreview;
  const canRunMovement = Boolean(
    isPreview && taskGuid && origin && destination && product && agent,
  );

  const insertAgent = useCallback(
    agentGuid =>
      withAction(() =>
        mutations.insertAgent({ operationGuid: guid, agentGuid }),
      ),
    [guid, mutations, withAction],
  );

  const updateAgent = useCallback(
    agentGuid =>
      withAction(() =>
        mutations.updateAgent({ operationGuid: guid, agentGuid }),
      ),
    [guid, mutations, withAction],
  );

  const prepareOrigin = useCallback(
    payload =>
      withAction(() =>
        mutations.prepareOrigin({
          operationGuid: guid,
          cellGuid: payload.cellGuid,
          count: payload.count,
        }),
      ),
    [guid, mutations, withAction],
  );

  const prepareDestination = useCallback(
    cellGuid =>
      withAction(() =>
        mutations.prepareDestination({
          operationGuid: guid,
          cellGuid,
        }),
      ),
    [guid, mutations, withAction],
  );

  const runMovement = useCallback(
    () => (taskGuid ? withAction(() => mutations.runMovement(taskGuid)) : null),
    [mutations, taskGuid, withAction],
  );

  return {
    loading,
    actionLoading,
    workspace,
    setWorkspace,
    refreshWorkspace,
    title,
    state,
    agent,
    product,
    origin,
    destination,
    taskGuid,
    isPreview,
    canMutate,
    canRunMovement,
    insertAgent,
    updateAgent,
    prepareOrigin,
    prepareDestination,
    runMovement,
    getAvailableAgents,
  };
};

export default useMovementWorkspace;
